import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const CONTACT_ENDPOINT =
  'https://forms.2merative.com/api/2merative-contact.php?form=contact'

function formDataToPayload(formData) {
  const payload = {}

  for (const [key, value] of formData.entries()) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      if (!Array.isArray(payload[key])) {
        payload[key] = [payload[key]]
      }

      payload[key].push(value)
    } else {
      payload[key] = value
    }
  }

  return payload
}

function RadioCardGroup({
  name,
  label,
  options,
  required = false,
}) {
  return (
    <div>
      <p className="block text-gray-700 font-semibold mb-3">
        {label}
        {required ? ' *' : ''}
      </p>

      <div className="grid gap-3">
        {options.map(option => {
          const id = `${name}-${option.value
            .replace(/\s+/g, '-')
            .toLowerCase()}`

          return (
            <label
              key={option.value}
              htmlFor={id}
              className="flex items-start gap-3 border rounded-xl p-4 cursor-pointer hover:border-blue-400 transition"
            >
              <input
                id={id}
                type="radio"
                name={name}
                value={option.value}
                required={required}
                className="mt-1 h-4 w-4 shrink-0"
              />

              <span className="text-gray-800 leading-6">
                {option.label}
              </span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

export default function ContactForm() {
  const navigate = useNavigate()

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async event => {
    event.preventDefault()

    if (submitting) return

    setSubmitting(true)
    setError('')

    const form = event.currentTarget
    const formData = new FormData(form)

    if (formData.get('_gotcha')) {
      navigate('/thank-you')
      return
    }

    const payload = formDataToPayload(formData)

    delete payload._gotcha

    payload.source = '2merative.com website contact form'
    payload.submittedAt = new Date().toISOString()

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const result = await response.json().catch(() => null)

      if (!response.ok || result?.success !== true) {
        throw new Error(
          result?.error || 'Form submission failed.'
        )
      }

      form.reset()
      navigate('/thank-you')
    } catch (submissionError) {
      console.error(
        '2merative contact form error:',
        submissionError
      )

      setError(
        submissionError instanceof Error
          ? submissionError.message
          : 'An error occurred. Please try again later.'
      )

      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-3xl mx-auto bg-white shadow-md rounded px-6 sm:px-8 pt-6 pb-8 mb-10 mt-10 space-y-6 text-left"
    >
      <h2 className="text-2xl font-semibold text-blue-800 text-center">
        Let’s talk about your website
      </h2>

      <p className="text-center text-gray-600 max-w-2xl mx-auto">
        Fill this out and we will get a clear picture of where
        your business is at, what you need, and what the right
        next step looks like.
      </p>

      <div className="hidden" aria-hidden="true">
        <label>
          Do not fill this out

          <input
            name="_gotcha"
            tabIndex={-1}
            autoComplete="off"
          />
        </label>
      </div>

      <div>
        <label
          htmlFor="contact-name"
          className="block text-gray-700 font-semibold mb-1"
        >
          Name *
        </label>

        <input
          id="contact-name"
          name="name"
          required
          autoComplete="name"
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <div>
        <label
          htmlFor="contact-email"
          className="block text-gray-700 font-semibold mb-1"
        >
          Email *
        </label>

        <input
          id="contact-email"
          type="email"
          name="email"
          required
          autoComplete="email"
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <div>
        <label
          htmlFor="contact-phone"
          className="block text-gray-700 font-semibold mb-1"
        >
          Phone *
        </label>

        <input
          id="contact-phone"
          type="tel"
          name="phone"
          required
          autoComplete="tel"
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <div>
        <label
          htmlFor="business-name"
          className="block text-gray-700 font-semibold mb-1"
        >
          Business name *
        </label>

        <input
          id="business-name"
          name="businessName"
          required
          autoComplete="organization"
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <RadioCardGroup
        name="projectType"
        label="What do you need help with?"
        required
        options={[
          {
            value: 'I need a new website',
            label: 'I need a new website',
          },
          {
            value:
              'I already have a website and need it improved',
            label:
              'I already have a website and need it improved',
          },
          {
            value: 'I am not sure yet',
            label: 'I am not sure yet',
          },
        ]}
      />

      <div>
        <label
          htmlFor="current-website"
          className="block text-gray-700 font-semibold mb-1"
        >
          If you already have a website, what is the website
          address?
        </label>

        <input
          id="current-website"
          name="currentWebsite"
          type="url"
          placeholder="https://"
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <RadioCardGroup
        name="websiteGoal"
        label="What is the main goal of the website?"
        required
        options={[
          {
            value: 'Get more enquiries',
            label: 'Get more enquiries',
          },
          {
            value: 'Look more professional',
            label: 'Look more professional',
          },
          {
            value:
              'Make it easier for customers to contact us',
            label:
              'Make it easier for customers to contact us',
          },
          {
            value: 'Replace an old or poor website',
            label: 'Replace an old or poor website',
          },
          {
            value: 'Support bookings or appointments',
            label: 'Support bookings or appointments',
          },
          {
            value: 'Sell products online',
            label: 'Sell products online',
          },
          {
            value: 'Other',
            label: 'Other',
          },
        ]}
      />

      <div>
        <label
          htmlFor="business-type"
          className="block text-gray-700 font-semibold mb-1"
        >
          What type of business are you?
        </label>

        <input
          id="business-type"
          name="businessType"
          placeholder="For example: chiropractor, tradie, café, club, consultant"
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <RadioCardGroup
        name="haveLogo"
        label="Do you already have branding or a logo?"
        options={[
          {
            value: 'Yes',
            label: 'Yes',
          },
          {
            value: 'No',
            label: 'No',
          },
          {
            value: 'Some, but it needs work',
            label: 'Some, but it needs work',
          },
        ]}
      />

      <div>
        <p className="block text-gray-700 font-semibold mb-3">
          What features do you need?
        </p>

        <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-700">
          {[
            'Contact form',
            'Online booking',
            'Online shop',
            'Blog or articles',
            'Portfolio or gallery',
            'Events or fixtures',
          ].map(feature => {
            const id = `feature-${feature
              .replace(/\s+/g, '-')
              .toLowerCase()}`

            return (
              <label
                key={feature}
                htmlFor={id}
                className="flex items-start gap-3 border rounded-xl p-4 cursor-pointer hover:border-blue-400 transition"
              >
                <input
                  id={id}
                  type="checkbox"
                  name="features"
                  value={feature}
                  className="mt-1 h-4 w-4 shrink-0"
                />

                <span className="text-gray-800 leading-6">
                  {feature}
                </span>
              </label>
            )
          })}
        </div>
      </div>

      <RadioCardGroup
        name="hostingHelp"
        label="Do you need help with domain or hosting?"
        options={[
          {
            value: 'Yes, I need help with both',
            label: 'Yes, I need help with both',
          },
          {
            value: 'I need help with hosting only',
            label: 'I need help with hosting only',
          },
          {
            value: 'I need help with domain only',
            label: 'I need help with domain only',
          },
          {
            value: 'No, I already have them sorted',
            label: 'No, I already have them sorted',
          },
          {
            value: 'Not sure',
            label: 'Not sure',
          },
        ]}
      />

      <RadioCardGroup
        name="timeline"
        label="What timeline are you working to?"
        options={[
          {
            value: 'As soon as possible',
            label: 'As soon as possible',
          },
          {
            value: 'Within 1 month',
            label: 'Within 1 month',
          },
          {
            value: 'Within 2 to 3 months',
            label: 'Within 2 to 3 months',
          },
          {
            value: 'No fixed timeline yet',
            label: 'No fixed timeline yet',
          },
        ]}
      />

      <RadioCardGroup
        name="budget"
        label="What budget range are you considering?"
        options={[
          {
            value: 'Under $2,000',
            label: 'Under $2,000',
          },
          {
            value: '$2,000 to $4,000',
            label: '$2,000 to $4,000',
          },
          {
            value: '$4,000 to $7,000',
            label: '$4,000 to $7,000',
          },
          {
            value: '$7,000+',
            label: '$7,000+',
          },
          {
            value: 'Not sure yet',
            label: 'Not sure yet',
          },
        ]}
      />

      <div>
        <label
          htmlFor="extra-notes"
          className="block text-gray-700 font-semibold mb-1"
        >
          Tell us a bit about what you need
        </label>

        <textarea
          id="extra-notes"
          name="extraNotes"
          rows={5}
          placeholder="Share anything useful here. What is not working, what you want the website to do better, or what kind of business situation you are in."
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      {error && (
        <p
          className="text-center text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}

      <div className="text-center">
        <button
          type="submit"
          disabled={submitting}
          className="bg-blue-700 hover:bg-blue-900 text-white font-bold py-3 px-6 rounded-lg transition duration-300 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Sending...' : 'Send Enquiry'}
        </button>
      </div>
    </form>
  )
}